import type { Meeting, MeetingDateGroupKey, MeetingGroup } from './meetingTypes.ts';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Returns the local midnight (start of day) for a given timestamp. */
const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * Classify a meeting's start time into a display bucket relative to `now`.
 *
 * - today: same calendar day as now
 * - yesterday: the calendar day before now
 * - thisWeek: within the previous 7 days (but not today/yesterday)
 * - earlier: older than 7 days
 */
export const getMeetingDateGroup = (
	startsAt: string,
	now: Date = new Date(),
): MeetingDateGroupKey => {
	const start = startOfDay(new Date(startsAt)).getTime();
	const today = startOfDay(now).getTime();
	const diffDays = Math.round((today - start) / DAY_MS);

	if (diffDays <= 0) {
		return 'today';
	}
	if (diffDays === 1) {
		return 'yesterday';
	}
	if (diffDays <= 6) {
		return 'thisWeek';
	}
	return 'earlier';
};

const GROUP_ORDER: MeetingDateGroupKey[] = ['today', 'yesterday', 'thisWeek', 'earlier'];

/**
 * Group meetings into ordered date buckets. Within each bucket, meetings are
 * sorted most-recent-first. Empty buckets are omitted.
 */
export const groupMeetings = (meetings: Meeting[], now: Date = new Date()): MeetingGroup[] => {
	const buckets = new Map<MeetingDateGroupKey, Meeting[]>();

	for (const meeting of meetings) {
		const key = getMeetingDateGroup(meeting.startsAt, now);
		const existing = buckets.get(key);
		if (existing) {
			existing.push(meeting);
		} else {
			buckets.set(key, [meeting]);
		}
	}

	return GROUP_ORDER.filter((key) => buckets.has(key)).map((key) => ({
		key,
		meetings: buckets
			.get(key)!
			.slice()
			.sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()),
	}));
};
